import { useState, useEffect, useRef } from "react";
import EnhancedTable from "components/utils/Table.js";
import CircularProgress from "@mui/material/CircularProgress";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useSnackbar } from "notistack";
import useAPI from "hooks/useAPI.js";
import { useParams } from "react-router-dom";
import FamilyService from "services/FamilyService.js";
import PatientFormService from "services/PatientFormService.js";
import PopUpModal from "components/utils/PopUpModal";
import AssessmentFormReport from "views/hospital/patient/assessment/AssessmentFormReport";
import DateUtils from "utils/DateUtils";
import SnackbarConstants from "utils/constants/Snackbar.js";
import { openDoctorAssessmentForm } from "utils/DoctorAssessmentFormUrls.js";

function AssessmentReportList() {
  const [familyMembers, setFamilyMembers] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [showReports, setShowReports] = useState(false);
  const [assessmentReports, setAssessmentReports] = useState([]);
  const [progressLoading, setProgressLoading] = useState(false);

  const [progressReportData, setProgressReportData] = useState(null);
  const progressReportModalRef = useRef(null);

  const { patientId: routePatientId } = useParams();
  const { enqueueSnackbar } = useSnackbar();

  const getMembersAPI = useAPI(
    FamilyService.getMembers,
    getMembersHandler,
    false,
    true
  );

  const getAssessmentReportsAPI = useAPI(
    PatientFormService.getAssessmentReports,
    getAssessmentReportsHandler
  );

  useEffect(() => {
    getMembersAPI.execute();
  }, []);

  function getMembersHandler(response) {
    const members = response.data || [];
    setFamilyMembers(members);

    if (routePatientId && !showReports && members.length > 0) {
      const numericId = Number(routePatientId);
      const target = members.find((m) => m.id === numericId);
      if (target) {
        handlePatientClick(target);
      }
    }
  }

  function getAssessmentReportsHandler(response) {
    setAssessmentReports(
      (response.data || []).map((form) => ({
        id: form.id,
        createdAt: form.createdAt,
        assessmentFormTypeEnum: form.assessmentFormTypeEnum,
        isMigrated: form.isMigrated,
      }))
    );
  }

  const handlePatientClick = (patient) => {
    setSelectedPatient(patient);
    setShowReports(true);
    getAssessmentReportsAPI.execute(patient.id);
  };

  const handleBackToPatients = () => {
    setSelectedPatient(null);
    setShowReports(false);
    setAssessmentReports([]);
  };

  const handleViewAssessment = (form) => {
    openDoctorAssessmentForm({
      formTypeEnum: form.assessmentFormTypeEnum,
      patientId: selectedPatient.id,
      assessmentFormId: form.id,
      isMigrated: form.isMigrated,
      viewMode: "read",
      patientView: true,
    });
  };

  const handleViewProgress = async () => {
    if (!selectedPatient || progressLoading) {
      return;
    }
    setProgressLoading(true);
    try {
      const response = await PatientFormService.getAssessmentProgressReportData(
        selectedPatient.id
      );
      setProgressReportData(response.data);
      progressReportModalRef.current.openModal();
    } catch (err) {
      enqueueSnackbar(
        err?.data?.message ||
          "No assessment progress is available yet for this patient.",
        SnackbarConstants.info
      );
    } finally {
      setProgressLoading(false);
    }
  };

  return (
    <div className="registration-forms-container page-shell">
      <div className="page-header">
        <div>
          <h1 className="page-title">Assessment Reports</h1>
          <p className="page-subtitle">
            Review assessment level summaries shared by your care team.
          </p>
        </div>
      </div>

      {!showReports ? (
        <Paper elevation={0} className="surface-card section-card">
          {getMembersAPI.status === "pending" ? (
            <div className="text-center">
              <CircularProgress className="text-center" sx={{ my: 20 }} />
            </div>
          ) : familyMembers.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 5 }}>
              <Typography variant="h6">No patient profiles found yet</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Add or confirm family member profiles to unlock Assessment
                Reports.
              </Typography>
            </Box>
          ) : (
            <div>
              <Typography variant="h4" className="mb-3">
                Select a patient to view assessment reports:
              </Typography>
              <Grid container spacing={2}>
                {familyMembers.map((member) => (
                  <Grid item xs={12} sm={6} md={4} key={member.id}>
                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={() => handlePatientClick(member)}
                      sx={{
                        justifyContent: "flex-start",
                        textAlign: "left",
                        p: 2,
                        borderRadius: 3,
                        textTransform: "none",
                        borderColor: "divider",
                        "&:hover": {
                          borderColor: "secondary.main",
                          backgroundColor: "rgba(15,118,110,0.06)",
                        },
                      }}
                    >
                      <div style={{ width: "100%" }}>
                        <Typography variant="subtitle1" noWrap>
                          {member.firstName} {member.lastName}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          noWrap
                        >
                          DOB: {member.dob}
                        </Typography>
                      </div>
                    </Button>
                  </Grid>
                ))}
              </Grid>
            </div>
          )}
        </Paper>
      ) : (
        <Paper elevation={0} className="surface-card section-card">
          <div className="mb-3">
            <Button
              variant="text"
              onClick={handleBackToPatients}
              sx={{ mb: 2 }}
            >
              ← Back to Patients
            </Button>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", sm: "center" }}
              spacing={2}
            >
              <Box>
                <Typography variant="h4">
                  Assessment Reports for {selectedPatient?.firstName}{" "}
                  {selectedPatient?.lastName}
                </Typography>
                <Stack direction="row" gap={1} sx={{ mt: 1 }}>
                  <Chip
                    size="small"
                    variant="outlined"
                    icon={<InfoOutlinedIcon />}
                    label="Shared assessments appear below after your clinician notifies you"
                  />
                </Stack>
              </Box>
              <Button
                variant="contained"
                color="primary"
                onClick={handleViewProgress}
                disabled={progressLoading}
                sx={{ textTransform: "none", flexShrink: 0 }}
              >
                {progressLoading ? "Loading..." : "View Progress"}
              </Button>
            </Stack>
          </div>

          {getAssessmentReportsAPI.status === "pending" ? (
            <div className="text-center">
              <CircularProgress className="text-center" sx={{ my: 20 }} />
            </div>
          ) : assessmentReports.length === 0 ? (
            <Box
              sx={{
                textAlign: "center",
                py: 4,
                border: "1px dashed",
                borderColor: "divider",
                borderRadius: 2,
              }}
            >
              <Typography variant="h6">No shared assessments yet</Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 1, mb: 2 }}
              >
                You can still open View Progress above. Shared assessment
                reports will appear here after your clinician notifies you.
              </Typography>
              <Button
                variant="outlined"
                onClick={handleBackToPatients}
                sx={{ textTransform: "none" }}
              >
                View Another Patient
              </Button>
            </Box>
          ) : (
            <EnhancedTable
              headings={assessmentTableHeadings}
              rowsData={assessmentReports.map((report, index) => ({
                ...report,
                summary: (
                  <div>
                    <Typography variant="subtitle1" fontWeight={600}>
                      Assessment Report #{index + 1}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {report.createdAt
                        ? DateUtils.formatDateTime(report.createdAt)
                        : ""}
                    </Typography>
                  </div>
                ),
                view: (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => handleViewAssessment(report)}
                    size="small"
                    sx={{ textTransform: "none" }}
                  >
                    View Assessment
                  </Button>
                ),
              }))}
              rowsPerPage={10}
            />
          )}
        </Paper>
      )}

      <PopUpModal ref={progressReportModalRef} modalWidth={"90%"}>
        {progressReportData && (
          <AssessmentFormReport
            reportData={progressReportData}
            hideEmailButton
          />
        )}
      </PopUpModal>

      <p className="inline-note">
        *View Progress shows levels across assessments. View Assessment opens a
        shared assessment summary.
      </p>
    </div>
  );
}

const assessmentTableHeadings = [
  {
    id: "summary",
    label: "Created At",
  },
  {
    id: "view",
    label: "",
  },
];

export default AssessmentReportList;
